import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { IDraftFromLowStockResult, IStockItem } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Sub-phase U.2a — data-table list of stock items. Row click → edit
// page. Keeps the "Draft POs from low-stock" helper button + its
// result dialog (which uses the full-page-on-phone class for the
// phone breakpoint).
@customElement('stock-list-page')
export class StockListPage extends Base {
  @state() private _items: IStockItem[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;

  @state() private _drafting = false;
  @state() private _draftResult: IDraftFromLowStockResult | null = null;
  @state() private _draftError: string | null = null;

  // Free-text search bound to <app-data-table>.freeTextFilter, filtered
  // by the existing filterFn on the data provider. Lives in component
  // state only — does NOT survive route change (intentional: opening
  // the page = empty filter).
  @state() private _search = '';

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        /* Full-height list-page shell — header anchored at top, the
           data-table fills the rest and scrolls internally. Page
           never grows beyond the app-shell's <main>; the table is
           the only thing that scrolls. */
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      /* Sticky search row — sits above the data table, never scrolls
         away. Uses the brand mono font so it visually matches the
         tabular data underneath. */
      .search-row {
        flex: none;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .search-row sl-input {
        flex: 1 1 auto;
        min-width: 0;
      }
      .search-row sl-input::part(input) {
        font-family: var(--font-mono);
        font-size: 0.9rem;
        letter-spacing: 0.01em;
      }
      .search-meta {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        letter-spacing: 0.06em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
      .money {
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;
    await this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.listStock(this.activeShop.shopName);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Failed to load stock.';
      } else {
        this._items = r.data;
      }
    } finally {
      this._loading = false;
    }
  }

  private _open(item: IStockItem) {
    router.navigate(resolveRouterPath(`stock/${encodeURIComponent(item.id)}`));
  }

  private _new() {
    router.navigate(resolveRouterPath('stock/new'));
  }

  private async _draftFromLowStock() {
    if (!this.activeShop || this._drafting) return;
    if (
      !confirm(
        'Draft purchase orders from low-stock items?\n\nThe system will create one Draft PO per primary supplier for every active stock item at or below its reorder level. Drafts can be reviewed and edited before placing.'
      )
    )
      return;

    this._drafting = true;
    this._draftError = null;
    this._draftResult = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.draftPurchaseOrdersFromLowStock(
        this.activeShop.shopName
      );
      if (!r.success || !r.data) {
        this._draftError = r.error ?? 'Could not draft purchase orders.';
        return;
      }
      this._draftResult = r.data;
      await this._load();
    } finally {
      this._drafting = false;
    }
  }

  private _closeDraftResult() {
    this._draftResult = null;
    this._draftError = null;
  }

  private _viewDraftedPOs() {
    this._closeDraftResult();
    router.navigate(resolveRouterPath('purchase-orders'));
  }

  private _renderDraftResultDialog() {
    if (!this._draftResult) return nothing;
    const r = this._draftResult;
    const drafted = r.draftedPurchaseOrderIds.length;
    const skipped = r.skippedItems.length;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Draft purchase orders — result"
        ?open=${true}
        @sl-after-hide=${() => this._closeDraftResult()}
      >
        ${
          drafted === 0 && skipped === 0
            ? html`
                <p>
                  <strong>No purchase orders drafted.</strong>
                  Every active stock item is above its reorder level (or none
                  have a reorder level set yet).
                </p>
              `
            : drafted === 0
              ? html`
                  <p>
                    <strong>No purchase orders drafted.</strong>
                    Items needed reordering, but each was missing config (see
                    below). Fix the missing pieces on the stock-edit page and
                    re-run the helper.
                  </p>
                `
              : html`
                  <p>
                    <strong>${drafted}</strong> draft purchase
                    order${drafted === 1 ? '' : 's'} created across ${drafted}
                    supplier${drafted === 1 ? '' : 's'}. Review and place them
                    on the Purchase orders page.
                  </p>
                `
        }
        ${
          skipped > 0
            ? html`
                <h4 style="margin-top: 12px;">Skipped items (${skipped})</h4>
                <ul style="margin: 4px 0 0 0; padding-left: 20px;">
                  ${r.skippedItems.map(
                    (s) => html`
                      <li>
                        <strong>${s.name}</strong>
                        <span style="opacity: 0.7;"> — ${s.reason}</span>
                      </li>
                    `
                  )}
                </ul>
                <p style="opacity: 0.7; font-size: 0.85rem; margin-top: 8px;">
                  Healthy / above-threshold items aren't listed here — only
                  those that
                  <em>would</em> have been drafted but were missing config.
                </p>
              `
            : nothing
        }

        <div
          slot="footer"
          style="display: flex; gap: 8px; justify-content: flex-end;"
        >
          ${
            drafted > 0
              ? html`
                  <sl-button
                    variant="primary"
                    @click=${() => this._viewDraftedPOs()}
                  >
                    View purchase orders
                  </sl-button>
                `
              : nothing
          }
          <sl-button variant="default" @click=${() => this._closeDraftResult()}>
            Close
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }

  private get _dataProvider() {
    return createArrayDataProvider<IStockItem>(() => this._items, {
      filterFn: (item, text) =>
        item.name.toLowerCase().includes(text) ||
        item.id.toLowerCase().includes(text),
    });
  }

  private _columns(): DataTableColumnDef[] {
    return [
      {
        title: '',
        fieldName: 'isActive',
        width: '36px',
        align: 'center',
        displayValue: (_t, item: IStockItem) =>
          item.isActive
            ? html`<sl-badge
                variant="success"
                pill
                style="--size: 6px;"
              ></sl-badge>`
            : html`<sl-badge
                variant="neutral"
                pill
                style="--size: 6px;"
              ></sl-badge>`,
      },
      {
        title: 'Name',
        fieldName: 'name',
        sortable: true,
        minWidth: '200px',
        displayValue: (_t, item: IStockItem) => html`
          <div>
            <strong>${item.name}</strong>
            <div style="font-size: 12px; opacity: 0.65;">${item.id}</div>
          </div>
        `,
      },
      {
        title: 'Unit',
        fieldName: 'baseUnit',
        width: '70px',
        sortable: true,
        align: 'center',
      },
      {
        title: 'On hand',
        fieldName: 'onHand',
        sortable: true,
        width: '110px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IStockItem) =>
          item.reorderLevel != null && item.onHand <= item.reorderLevel
            ? html`<span
                style="color: var(--sl-color-warning-700); font-weight: 600;"
                >${item.onHand}</span
              >`
            : html`<span>${item.onHand}</span>`,
      },
      {
        title: 'Reorder at',
        fieldName: 'reorderLevel',
        sortable: true,
        width: '110px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IStockItem) =>
          item.reorderLevel != null ? String(item.reorderLevel) : '—',
      },
      {
        title: 'Suppliers',
        fieldName: 'supplierLinks',
        width: '100px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IStockItem) => {
          const n = item.supplierLinks.length;
          if (n === 0) return html`<span style="opacity: 0.55;">—</span>`;
          return html`<span>${n}</span>`;
        },
      },
      {
        title: 'Cost',
        fieldName: 'cost',
        sortable: true,
        width: '90px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, item: IStockItem) =>
          item.cost > 0 ? item.cost.toFixed(4) : '—',
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Stock</h2>
        ${
          isOwner
            ? html`
                <div style="display: flex; gap: 8px;">
                  <sl-button
                    variant="text"
                    size="small"
                    @click=${() => router.navigate(resolveRouterPath('stock/locations'))}
                  >
                    Locations
                  </sl-button>
                  <sl-button
                    variant="default"
                    size="small"
                    ?loading=${this._drafting}
                    ?disabled=${this._drafting || this._items.length === 0}
                    @click=${() => this._draftFromLowStock()}
                  >
                    <sl-icon slot="prefix" name="cart-plus"></sl-icon>
                    Draft POs from low-stock
                  </sl-button>
                  <sl-button
                    variant="primary"
                    size="small"
                    @click=${() => this._new()}
                  >
                    <sl-icon slot="prefix" name="plus-circle"></sl-icon>
                    New stock item
                  </sl-button>
                </div>
              `
            : nothing
        }
      </header>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._draftError
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._draftError = null)}
              >${this._draftError}</sl-alert
            >`
          : nothing
      }
      ${this._renderDraftResultDialog()}
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._items.length === 0
            ? html`
                <div class="empty">
                  <p>No stock items yet.</p>
                  ${
                    isOwner
                      ? html`
                          <sl-button
                            variant="primary"
                            @click=${() => this._new()}
                          >
                            Add your first stock item
                          </sl-button>
                        `
                      : nothing
                  }
                </div>
              `
            : html`
                <div class="search-row">
                  <sl-input
                    placeholder="Search stock — name, location, supplier…"
                    clearable
                    .value=${this._search}
                    @sl-input=${(e: Event) => {
                      this._search = (e.target as HTMLInputElement).value;
                    }}
                  >
                    <sl-icon slot="prefix" name="search"></sl-icon>
                  </sl-input>
                  ${
                    this._search
                      ? html`<span class="search-meta">FILTERING</span>`
                      : html`<span class="search-meta"
                          >${this._items.length} ITEMS</span
                        >`
                  }
                </div>
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns()}
                  .dataProvider=${this._dataProvider}
                  .freeTextFilter=${this._search}
                  .keyevent=${(item: IStockItem) => item.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: IStockItem }>) =>
                    this._open(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
